/**
 * @file    useMainViewProps.ts
 * @scope   VIEW
 * @summary Assembles the memoized prop groups the drawer main tab feeds to its sections.
 * @consumes The drawer domain hooks' state and handlers
 * @produces A `MainViewProps` object whose groups keep stable references between renders.
 * @sideEffects None — presentation wiring only.
 * @notes WHY memoized groups: the sections are `React.memo`-wrapped, so a stable reference per
 *        group lets React skip the ones whose inputs did not change. Rebuilding these literals
 *        inline in the orchestrator re-rendered the whole tab on every drawer render.
 */
import { useCallback, useMemo } from 'react';
import { MainViewProps } from './MainView';
import {
  chartsWithTextControllers,
  chartsWithTrend,
  dataGridRulesWithTrend,
} from '../../common/constants/chartCapabilities';

/** Inputs of {@link useMainViewProps}, grouped by the domain hook that owns them. */
export type UseMainViewPropsParams = {
  customWidget: any;
  loading: boolean;
  /** True while the selected chart's Config (questions, calculations, rules) is being fetched. */
  loadingConfig: boolean;
  /** False until the chart type has been resolved; its Config cannot be requested before. */
  hasCurrentAlias: boolean;
  isDiyWidgetEngine: boolean;
  /** Title. */
  requiredDisplayName: boolean;
  setWidgetTitle: (title: string) => void;
  setRequiredDisplayName: (required: boolean) => void;
  /** Chart type. */
  chartTypes: any;
  onChartTypeChange: (option: any) => void;
  hasEnterpriseV2Config: (config: any) => boolean;
  /** Trend. */
  chartTrend: any;
  selectedChartTrend: any;
  onTrendChange: (option: any) => void;
  onTrendCountChange: (count: any) => void;
  setSelectedChartTrend: (trend: any) => void;
  /** Questions / calculations. */
  applicableRules: any;
  chartQuestions: any;
  chartCalculations: any;
  filteredQuestions: any;
  filteredCalculations: any;
  selectedQuestions: any;
  selectedCalculations: any;
  onQuestionChange: (value: any) => void;
  onCalculationChange: (value: any) => void;
  onQuestionsDragEnd: (result: any) => void;
  onCalculationsDragEnd: (result: any) => void;
  showQuestionCalculation: (type: any) => boolean;
  requiredQuestions: boolean;
  requiredCalculations: boolean;
  requiredQuestionsCalculations: boolean;
  touchedQC: boolean;
  setTouchedQC: (touched: boolean) => void;
  /** Enterprise grid + query builder. */
  availableCollections: any;
  setSelectedPartition: (partition: any) => void;
  setCurrentTab: (tab: any) => void;
  setQueryData: (data: any) => void;
  /** Colors. */
  initColor: any;
  initColorFamily: any;
  setSelectedColor: (color: any) => void;
  setSelectedColorFamily: (family: any) => void;
  /** Text block. */
  setTextBlockData: (data: any) => void;
  setTextContentIsValid: (valid: boolean) => void;
  /** Options + filters. */
  optionsHandlers: any;
  ruleName: string;
  chartFilters: any;
  applyDateRange: (range: any) => void;
  selectedFilteringDateRange: any;
  useDiyHierarchyFilters?: boolean;
  hierarchyFiltersValues: any;
  onHierarchyFiltersChange: (values: any) => void;
  useAdvancedFilters?: boolean;
  advancedFiltersValues: any;
  onAdvancedFiltersChange: (values: any) => void;
};

export const useMainViewProps = (params: UseMainViewPropsParams): MainViewProps => {
  const {
    customWidget,
    loading,
    isDiyWidgetEngine,
    touchedQC,
    setTouchedQC,
    requiredQuestionsCalculations,
  } = params;

  const markQuestionsCalculationsTouched = useCallback(() => {
    if (!touchedQC) setTouchedQC(true);
  }, [touchedQC, setTouchedQC]);

  const title = useMemo(
    () => ({
      required: params.requiredDisplayName,
      onTitleChange: params.setWidgetTitle,
      onRequiredChange: params.setRequiredDisplayName,
    }),
    [params.requiredDisplayName, params.setWidgetTitle, params.setRequiredDisplayName]
  );

  const chartType = useMemo(
    () => ({
      options: params.chartTypes,
      onChange: params.onChartTypeChange,
      hasEnterpriseV2Config: params.hasEnterpriseV2Config,
    }),
    [params.chartTypes, params.onChartTypeChange, params.hasEnterpriseV2Config]
  );

  const trend = useMemo(
    () => ({
      options: params.chartTrend,
      onChange: params.onTrendChange,
      selected: params.selectedChartTrend,
      onCountChange: params.onTrendCountChange,
      setSelected: params.setSelectedChartTrend,
    }),
    [
      params.chartTrend,
      params.onTrendChange,
      params.selectedChartTrend,
      params.onTrendCountChange,
      params.setSelectedChartTrend,
    ]
  );

  // LEGACY-ONLY: Medusa-query and Enterprise widgets pick their measures elsewhere.
  const isLegacyMeasureWidget = !customWidget.hasMedusaQuery && !isDiyWidgetEngine;

  // WHY three conditions: questions, calculations and the rules behind the show flags all come
  // from the chart Config, fetched on a second channel only after the chart type resolves.
  // Following the catalog `loading` alone let the pickers pop in once the Config landed.
  const isMeasuresLoading =
    isLegacyMeasureWidget &&
    !chartsWithTextControllers.includes(customWidget.alias) &&
    (loading || params.loadingConfig || !params.hasCurrentAlias);

  const questionsCalculations = useMemo(
    () => ({
      loading: isMeasuresLoading,
      applicableRules: params.applicableRules,
      onTouch: markQuestionsCalculationsTouched,
      showQuestions: Boolean(
        isLegacyMeasureWidget &&
          params.chartQuestions?.length &&
          params.showQuestionCalculation('questions')
      ),
      questionOptions: params.filteredQuestions,
      selectedQuestions: params.selectedQuestions,
      onQuestionChange: params.onQuestionChange,
      onQuestionsDragEnd: params.onQuestionsDragEnd,
      questionsInvalid: Boolean(
        !customWidget.hasMedusaQuery &&
          touchedQC &&
          params.requiredQuestions &&
          requiredQuestionsCalculations
      ),
      showCalculations: Boolean(
        isLegacyMeasureWidget &&
          params.chartCalculations?.length &&
          params.showQuestionCalculation('calculations')
      ),
      calculationOptions: params.filteredCalculations,
      selectedCalculations: params.selectedCalculations,
      onCalculationChange: params.onCalculationChange,
      onCalculationsDragEnd: params.onCalculationsDragEnd,
      calculationsInvalid: Boolean(
        !customWidget.hasMedusaQuery &&
          touchedQC &&
          params.requiredCalculations &&
          requiredQuestionsCalculations
      ),
    }),
    [
      isMeasuresLoading,
      isLegacyMeasureWidget,
      customWidget.hasMedusaQuery,
      markQuestionsCalculationsTouched,
      touchedQC,
      requiredQuestionsCalculations,
      params.applicableRules,
      params.chartQuestions,
      params.chartCalculations,
      params.showQuestionCalculation,
      params.filteredQuestions,
      params.filteredCalculations,
      params.selectedQuestions,
      params.selectedCalculations,
      params.onQuestionChange,
      params.onCalculationChange,
      params.onQuestionsDragEnd,
      params.onCalculationsDragEnd,
      params.requiredQuestions,
      params.requiredCalculations,
    ]
  );

  const enterpriseGrid = useMemo(
    () => ({
      availableOptions: params.availableCollections,
      setSelectedPartition: params.setSelectedPartition,
      setCurrentTab: params.setCurrentTab,
    }),
    [params.availableCollections, params.setSelectedPartition, params.setCurrentTab]
  );

  const query = useMemo(
    () => ({ availableOptions: params.availableCollections, onChange: params.setQueryData }),
    [params.availableCollections, params.setQueryData]
  );

  const color = useMemo(
    () => ({
      initColor: params.initColor,
      initColorFamily: params.initColorFamily,
      onSelectColor: params.setSelectedColor,
      onSelectColorFamily: params.setSelectedColorFamily,
    }),
    [
      params.initColor,
      params.initColorFamily,
      params.setSelectedColor,
      params.setSelectedColorFamily,
    ]
  );

  const textBlock = useMemo(
    () => ({ onDataChange: params.setTextBlockData, onValidChange: params.setTextContentIsValid }),
    [params.setTextBlockData, params.setTextContentIsValid]
  );

  const options = useMemo(() => ({ handlers: params.optionsHandlers }), [params.optionsHandlers]);

  const filters = useMemo(
    () => ({
      widgetId: customWidget.id,
      // The calendar is replaced by the trend selector on trend-capable charts.
      showCalendar:
        !chartsWithTrend.includes(customWidget.alias as string) &&
        !dataGridRulesWithTrend.includes(params.ruleName),
      chartFilters: params.chartFilters,
      onSelectDateRange: params.applyDateRange,
      dateSelection: params.selectedFilteringDateRange,
      currentSelection: customWidget.customWidgetConfig?.filters?.currentSelection ?? {},
      showHierarchyFilters: params.useDiyHierarchyFilters,
      hierarchyFiltersValues: params.hierarchyFiltersValues,
      onHierarchyFiltersChange: params.onHierarchyFiltersChange,
      showAdvancedFilters: params.useAdvancedFilters,
      advancedFiltersValues: params.advancedFiltersValues,
      onAdvancedFiltersChange: params.onAdvancedFiltersChange,
    }),
    [
      customWidget.id,
      customWidget.alias,
      customWidget.customWidgetConfig,
      params.ruleName,
      params.chartFilters,
      params.applyDateRange,
      params.selectedFilteringDateRange,
      params.useDiyHierarchyFilters,
      params.hierarchyFiltersValues,
      params.onHierarchyFiltersChange,
      params.useAdvancedFilters,
      params.advancedFiltersValues,
      params.onAdvancedFiltersChange,
    ]
  );

  return {
    title,
    chartType,
    trend,
    questionsCalculations,
    enterpriseGrid,
    query,
    color,
    textBlock,
    options,
    filters,
  };
};
